import { useState } from "react";
import { Link } from "wouter";
import { Loader2, ShieldCheck, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import vexisLogo from "@assets/vexis-logo-2026.jpg";

const BG = "#05070a";
const INDIGO = "#6366f1";
const MUTED = "#94a3b8";
const BORDER = "rgba(99,102,241,0.18)";

export default function OpsLogin() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || loading) return;
    setLoading(true);
    setRateLimited(false);
    try {
      const res = await fetch("/api/auth/ops-magic-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      if (res.status === 429 || res.status === 403) {
        setRateLimited(true);
        return;
      }
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-4"
      style={{ backgroundColor: BG }}
    >
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-10">
          <img src={vexisLogo} alt="VEXIS" className="h-10 w-auto" />
        </div>

        <div
          className="rounded-2xl p-8 space-y-6"
          style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${BORDER}` }}
        >
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-4 h-4 text-indigo-400 flex-shrink-0" />
            <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: MUTED }}>
              Ops Access
            </span>
          </div>

          {submitted ? (
            <div className="space-y-3 text-center py-2">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center mx-auto"
                style={{ backgroundColor: "rgba(99,102,241,0.12)" }}
              >
                <Mail className="w-5 h-5 text-indigo-400" />
              </div>
              <p className="font-semibold text-white text-base">Check your inbox</p>
              <p className="text-sm" style={{ color: MUTED }}>
                If this email has admin access, a link has been sent. It expires in 15 minutes.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-white" htmlFor="ops-email">
                  Email address
                </label>
                <Input
                  id="ops-email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@vexistalentagency.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="input-ops-email"
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-indigo-500"
                />
              </div>
              {rateLimited && (
                <p className="text-sm text-red-400 text-center pb-1">
                  Too many attempts. Please wait 15 minutes and try again.
                </p>
              )}
              <Button
                type="submit"
                disabled={loading || !email.trim()}
                data-testid="button-ops-send-link"
                className="w-full font-semibold text-white"
                style={{ backgroundColor: INDIGO }}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending…
                  </>
                ) : (
                  "Send Access Link"
                )}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
